import { useAtomValue } from "jotai";
import React from "react";
import { useTranslation } from "react-i18next";

import { cn } from "~/components/ui/cn";
import { DollarCircleIcon } from "~/components/ui/icons/dollar-circle";
import { EuroCircleIcon } from "~/components/ui/icons/euro-circle";
import { InformationIcon } from "~/components/ui/icons/information";
import { PercentIcon } from "~/components/ui/icons/percent";
import { ReturnIcon } from "~/components/ui/icons/return";
import { TagReturnIcon } from "~/components/ui/icons/tag-return";
import {
  StatisticsContent,
  StatisticsGroup,
  StatisticsGroupStatistics,
  StatisticsTitle,
} from "~/components/ui/statistics";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { StoreCurrency, useReturnsQuery } from "~/graphql/graphql.gen";
import { activeSaleState } from "~/stores/sales";
import { formatNumber } from "~/utils/number";

export const Returns = () => {
  const { i18n, t } = useTranslation();

  const activeSale = useAtomValue(activeSaleState);

  const { data: returnsData } = useReturnsQuery(
    {
      saleId: activeSale?.id ?? "",
    },
    {
      enabled: !!activeSale?.id,
      refetchOnWindowFocus: false,
    },
  );

  if (!returnsData?.returns.returns?.length) {
    return (
      <div className="flex flex-row items-center gap-2 rounded-lg border border-[#ECECEC] p-6">
        <ReturnIcon className="size-6" />
        <p className="text-gray-700">{t("analytics.returns_empty")}</p>
      </div>
    );
  }

  return (
    <div data-slot="returns">
      <div className="row-stats grid grid-cols-1 gap-4 lg:grid-cols-2">
        <StatisticsGroup>
          <StatisticsGroupStatistics>
            <StatisticsTitle>
              <ReturnIcon className="size-4" />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="border-b border-dashed border-gray-300 text-xs font-semibold uppercase leading-none tracking-wider text-gray-700 hover:border-solid">
                    {t("analytics.returns_total_orders")}
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <div className="p-1">
                      {t("analytics.return_tooltip_total_orders")}
                    </div>
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </StatisticsTitle>
            <StatisticsContent>
              <span className="text-4xl font-bold text-gray-900">
                {returnsData.returns.total_orders}
              </span>
            </StatisticsContent>
          </StatisticsGroupStatistics>
          <StatisticsGroupStatistics>
            <StatisticsTitle>
              <PercentIcon className="size-4" />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="border-b border-dashed border-gray-300 text-xs font-semibold uppercase leading-none tracking-wider text-gray-700 hover:border-solid">
                    {t("analytics.returns_rate")}
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <div className="p-1">
                      {t("analytics.tooltip_returns_rate")}
                    </div>
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </StatisticsTitle>
            <StatisticsContent className="gap-1">
              <span className="text-4xl font-bold text-gray-900">
                {formatNumber(
                  (returnsData.returns.percentage ?? 0) * 100,
                  i18n.language === "fr" ? "fr" : "en",
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  },
                )}
              </span>

              <span className="text-2xl font-bold text-gray-700">%</span>
            </StatisticsContent>
          </StatisticsGroupStatistics>
          <StatisticsGroupStatistics>
            <StatisticsTitle>
              {activeSale?.currency === StoreCurrency.Usd ? (
                <DollarCircleIcon className="size-4 shrink-0" />
              ) : (
                <EuroCircleIcon className="size-4 shrink-0" />
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="border-b border-dashed border-gray-300 text-xs font-semibold uppercase leading-none tracking-wider text-gray-700 hover:border-solid">
                    {t("analytics.returns_total_refund")}
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <div className="p-1">
                      {t("analytics.tooltip_total_refund")}
                    </div>
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </StatisticsTitle>
            <StatisticsContent
              className={cn("gap-1", {
                "flex-row-reverse justify-end": i18n.language === "en",
              })}
            >
              <span className="text-4xl font-bold text-gray-900">
                {formatNumber(
                  (returnsData.returns.total_sum ?? 0) / 100,
                  i18n.language === "fr" ? "fr" : "en",
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  },
                )}
              </span>

              <span className="text-2xl font-bold text-gray-700">
                {activeSale?.currency === StoreCurrency.Usd ? "$" : "€"}
              </span>
            </StatisticsContent>
          </StatisticsGroupStatistics>
        </StatisticsGroup>
        <div className="w-full">
          <div className="rounded border border-gray-300 p-6 pb-0">
            <div>
              <div className="flex flex-row items-center justify-between">
                <div className="flex items-center">
                  <TagReturnIcon className="size-4" />

                  <Tooltip>
                    <TooltipTrigger className="ml-2.25 cursor-pointer border-b border-dashed border-gray-300 text-xs font-semibold uppercase tracking-wider text-gray-700 hover:border-solid">
                      {t("analytics.returns_products")}
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <div className="p-1">
                        {t("analytics.tooltip_returns_products")}
                      </div>
                      <TooltipArrow />
                    </TooltipContent>
                  </Tooltip>
                </div>

                <p className="text-xs text-gray-500">
                  {t("analytics.returns_count")}
                </p>
              </div>
              <div className="space-y-3 py-4">
                {(returnsData.returns.products ?? [])
                  .filter((p) => !!p)
                  .map((p, i) => (
                    <div
                      className="flex flex-row items-center space-x-2"
                      key={i}
                    >
                      <img
                        className="size-14 rounded border border-gray-300"
                        src={p.image}
                        alt=""
                      />
                      <div className="flex-1 space-y-1">
                        <p className="text-sm text-gray-700">{p.description}</p>
                        {p.options ? (
                          <p className="text-xs text-gray-500">
                            {p.options
                              .filter((o): o is string => !!o)
                              .join(" • ")}
                          </p>
                        ) : null}
                      </div>
                      <p className="float-right text-xs font-semibold text-gray-500">
                        {p.sum}
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
        {(returnsData.returns.returns ?? []).length ? (
          <div className="lg:col-span-2">
            <div className="rounded border border-gray-300 p-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-row items-center justify-between">
                  <div className="flex items-center">
                    <InformationIcon className="size-4" />

                    <Tooltip>
                      <TooltipTrigger className="ml-2.25 cursor-pointer border-b border-dashed border-gray-300 text-xs font-semibold uppercase tracking-wider text-gray-700 hover:border-solid">
                        {t("analytics.returns_reason")}
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <div className="p-1">
                          {t("analytics.tooltip_returns_reason")}
                        </div>
                        <TooltipArrow />
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <p className="text-xs text-gray-500">
                    {t("analytics.returns_count")}
                  </p>
                </div>
                <div className="flex flex-col gap-3.5">
                  {(returnsData.returns.returns ?? [])
                    .filter((r) => !!r)
                    .map((r, i) => (
                      <p key={r.code} className="text-gray-900">
                        <span className="text-gray-500">{i + 1}</span>
                        <span className="px-2 text-gray-500">—</span>
                        <span className="text-sm font-normal">
                          {t("analytics.return_code_" + r.code)}{" "}
                          {r.subCode
                            ? " - " +
                              t(
                                "analytics.return_code_sub_" +
                                  r.subCode.toLowerCase(),
                              )
                            : ""}
                        </span>

                        <span className="float-right text-xs font-semibold text-gray-500">
                          {r.sum}
                        </span>
                      </p>
                    ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
